import React, { useMemo, useState } from "react"
import { GatsbyImage } from "gatsby-plugin-image"
import loadable from "@loadable/component"
import "leaflet/dist/leaflet.css"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import Section from "../layout/section"
import { FixedContainer } from "../layout/container"
import ExternalLink from "../layout/externalLink"

const _ = require(`lodash`)

const LeafletAlumniMap = loadable(() => import("./LeafletAlumniMap"), {
  ssr: false,
})

const hasValue = value => value !== "" && value !== undefined && value !== null

export const GroupStats = ({ hIndex, pubCount, timesCited }) => (
  <div className="level">
    {pubCount && (
      <div className="level-item has-text-centered">
        <div>
          <p className="heading">Peer-Reviewed Publications</p>
          <p className="title">{pubCount}</p>
        </div>
      </div>
    )}
    {hIndex && (
      <div className="level-item has-text-centered">
        <div>
          <p className="heading">H-Index</p>
          <p className="title">{hIndex}</p>
        </div>
      </div>
    )}
    {timesCited && (
      <div className="level-item has-text-centered">
        <div>
          <p className="heading">Total Times Cited</p>
          <p className="title">{timesCited}</p>
        </div>
      </div>
    )}
  </div>
)

export const GroupMember = ({ member, className }) => {
  const {
    name,
    profilePic,
    email,
    office,
    url,
    jobTitle,
    altName,
    cv,
    slug,
    hIndex,
    pubCount,
    timesCited,
    researcherId,
    ORCID,
    googleScholar,
  } = member

  const columnClass = className === undefined ? "" : `bio-${className}`

  const hasResearchLinks =
    hasValue(researcherId) || hasValue(ORCID) || hasValue(googleScholar)

  const hasStats = hasValue(hIndex) || hasValue(pubCount) || hasValue(timesCited)
  const hasCv = hasValue(cv)

  const img =
    profilePic &&
    profilePic.childImageSharp &&
    profilePic.childImageSharp.gatsbyImageData

  return (
    <div className={`bio card ${columnClass}`}>
      <div className="card-image">
        <figure className="image round-image">
          {img ? (
            <GatsbyImage image={img} alt={name} />
          ) : (
            <div style={{ padding: "3rem", textAlign: "center" }}>
              No photo
            </div>
          )}
        </figure>
      </div>

      <div className="card-content">
        <div className="media">
          <div className="media-content">
            <p className="title is-4">{name}</p>
            {jobTitle && <p className="subtitle is-6">{jobTitle}</p>}
          </div>

          {hasCv && (
            <div className="media-right">
              <p>
                <ExternalLink className="button" path={cv} title="CV">
                  <span className="icon is-small">
                    <FontAwesomeIcon icon={["far", "file-pdf"]} title="PDF" />
                  </span>
                  <span>CV</span>
                </ExternalLink>
              </p>
            </div>
          )}
        </div>

        <div className="content">
          <dl className="details">
            {email && (
              <span>
                <dt>Email:</dt>
                <dd>
                  <a href={`mailto:${email}`}>{email}</a>
                </dd>
              </span>
            )}

            {url && (
              <span>
                <dt>URL:</dt>
                <dd>
                  <a href={url}>{url}</a>
                </dd>
              </span>
            )}

            {office && (
              <span>
                <dt>Office:</dt>
                <dd>{office}</dd>
              </span>
            )}
          </dl>

          {hasResearchLinks && (
            <dl className="research">
              {researcherId && (
                <span>
                  <dt>ResearcherID:</dt>
                  <dd>
                    <span id={`rid-${slug}`}>
                      <ExternalLink
                        path={`https://www.webofscience.com/wos/author/rid/${researcherId}`}
                        title="ResearcherID"
                      >
                        {researcherId}
                      </ExternalLink>
                    </span>
                  </dd>
                </span>
              )}

              {ORCID && (
                <span>
                  <dt>ORCID:</dt>
                  <dd>
                    <ExternalLink
                      path={`http://orcid.org/${ORCID}`}
                      title="ORCID"
                    >
                      {ORCID}
                    </ExternalLink>
                  </dd>
                </span>
              )}

              {googleScholar && (
                <span>
                  <dt>Google Scholar:</dt>
                  <dd>
                    <ExternalLink
                      path={`https://scholar.google.com/citations?user=${googleScholar}&hl=en`}
                      title="Google Scholar"
                    >
                      {altName || name}
                    </ExternalLink>
                  </dd>
                </span>
              )}
            </dl>
          )}
        </div>
      </div>
    </div>
  )
}

const MemberGrid = ({ members, className }) => {
  if (!members || members.length === 0) return null

  return (
    <div className="columns is-centered is-multiline">
      {members.map(member => (
        <div className="column" key={member.id || member.slug || member.name}>
          <GroupMember member={member} className={className} />
        </div>
      ))}
    </div>
  )
}

const AlumniTable = ({ alumni }) => {
  const [sortColumn, setSortColumn] = useState("name")
  const [sortDirection, setSortDirection] = useState("asc")

  const columns = [
    { key: "name", label: "Name" },
    { key: "groupStatus", label: "Position" },
    { key: "departureYear", label: "Year" },
    { key: "currentJobTitle", label: "Current Title" },
    { key: "currentAffiliation", label: "Current Affiliation" },
  ]

  const sortedAlumni = useMemo(() => {
    const sortable = [...alumni]

    sortable.sort((a, b) => {
      const valA = a[sortColumn] || ""
      const valB = b[sortColumn] || ""

      if (sortColumn === "departureYear") {
        const numA = Number(valA) || 0
        const numB = Number(valB) || 0

        return sortDirection === "asc" ? numA - numB : numB - numA
      }

      return sortDirection === "asc"
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA))
    })

    return sortable
  }, [alumni, sortColumn, sortDirection])

  const handleSort = column => {
    if (sortColumn === column.key) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
    } else {
      setSortColumn(column.key)
      setSortDirection("asc")
    }
  }

  const sortArrow = column => {
    if (sortColumn !== column.key) return ""
    return sortDirection === "asc" ? " ▲" : " ▼"
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={tableStyle}>
        <thead>
          <tr>
            {columns.map(column => (
              <th
                key={column.key}
                onClick={() => handleSort(column)}
                style={{
                  ...thStyle,
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                {column.label}
                {sortArrow(column)}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {sortedAlumni.map(member => (
            <tr key={member.id || member.slug || member.name}>
              <td style={tdStyle}>
                {member.url ? (
                  <a
                    href={member.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {member.altName || member.name}
                  </a>
                ) : (
                  member.altName || member.name
                )}
              </td>

              <td style={tdStyle}>
                {member.groupStatus || member.role || "Alumni"}
              </td>

              <td style={tdStyle}>{member.departureYear || ""}</td>

              <td style={tdStyle}>{member.currentJobTitle || ""}</td>
              
			  <td style={tdStyle}>{member.currentAffiliation || ""}</td>
             
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export const GroupList = ({ members }) => {
  const currentMembers = members.filter(member => member.status === "Current")
  const alumniMembers = members.filter(member => member.status === "Alumni")

  const currentGroups = _.groupBy(currentMembers, "role")

  const leaders = _.sortBy(currentGroups["Group Leader"] || [], "name")
  const postdocs = _.sortBy(currentGroups["Postdoc"] || [], "name")
  const students = _.sortBy(currentGroups["Graduate Student"] || [], "name")
  const scientists = _.sortBy(currentGroups["Scientist"] || [], "name")
  const undergrads = _.sortBy(currentGroups["Undergraduate Student"] || [], "name")
  const alumni = _.sortBy(alumniMembers, "name")

  return (
    <Section>
      <FixedContainer>
	     <h2 
	       className="title is-2 has-text-centered"
	       style={{ marginBottom: "4rem", clear: "both" }}
	     >
	       Current Group Members
	     </h2>
         
	     {leaders.length > 0 && (
	       <div className="columns is-centered" style={{ clear: "both" }}>
	         <div className="column is-half">
	           {leaders.map(member => (
	             <GroupMember
	               member={member}
	               key={member.id || member.slug || member.name}
	               className="gl"
	             />
	           ))}
	         </div>
	       </div>
	     )}

        {students.length > 0 && (
          <>
            <MemberGrid members={students} className="gs" />
          </>
        )}

        {alumni.length > 0 && (
          <>
            <hr style={{ margin: "4rem 0" }} />

            <h2 className="title is-2 has-text-centered">
              Alumni List and Map
            </h2>

            <LeafletAlumniMap alumni={alumni} />

            <AlumniTable alumni={alumni} />
          </>
        )}
      </FixedContainer>
    </Section>
  )
}

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  marginTop: "1.5rem",
}

const thStyle = {
  padding: "0.75rem",
  textAlign: "left",
  borderBottom: "2px solid #333",
  backgroundColor: "#f5f5f5",
  fontWeight: "bold",
  whiteSpace: "nowrap",
}

const tdStyle = {
  padding: "0.75rem",
  borderBottom: "1px solid #ddd",
  verticalAlign: "top",
}

export default GroupList